import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBrandTheme } from "../../context/BrandThemeContext";
import { useCustomerWorkspace } from "../../context/CustomerWorkspaceContext";
import { useChatWorkspace } from "../../context/ChatWorkspaceContext";
import { AppearanceMenu } from "../../context/ThemeManager";
import CustomerDropdown from "../../components/Shared/CustomerDropdown";
import ConnectionAlertBell from "../../components/layout/ConnectionAlertBell";
import ConversationSidebar from "../../components/AIAgents/ChatWithAgent/ConversationSidebar";
import { isSuperAdmin } from "../../utils/permission";
import { Customer } from "../../types/customer";

interface SidebarProps {
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  onClose,
  collapsed = false,
  onToggleCollapse,
}) => {
  const { logout, user, userGroups, idToken } = useAuth();
  const { setCustomerId } = useBrandTheme();
  const { selectedCustomerId, setSelectedCustomerId, setSelectedCustomerName } = useCustomerWorkspace();
  const { threadListVersion } = useChatWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [historySearchOpen, setHistorySearchOpen] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const closeOnOutside = (e: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(e.target as Node)
      ) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [accountMenuOpen]);

  const canManageAllUsers = isSuperAdmin(userGroups);

  const handleLogout = () => {
    setAccountMenuOpen(false);
    logout();
    navigate("/login");
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose?.();
    setAccountMenuOpen(false);
  };

  const handleCustomerChange = useCallback((customer: Customer | null) => {
    const id = customer?.customer_id ?? null;
    let name = customer?.business_name ?? null;
    if (customer && customer.city && customer.state) {
      name = `${customer.business_name} - ${customer.city}, ${customer.state}`;
    }
    setSelectedCustomerId(id);
    setSelectedCustomerName(name);
    if (customer) {
      setCustomerId(customer.customer_id);
    } else {
      setCustomerId(null);
    }
    // On Chat home: switching business clears deep link; same business keeps agent/thread in URL
    if (location.pathname === "/" || location.pathname === "") {
      if (id) {
        const prevCustomer = searchParams.get("customerId");
        if (prevCustomer && prevCustomer !== id) {
          navigate(`/?customerId=${encodeURIComponent(id)}`, { replace: true });
        } else {
          const next = new URLSearchParams(searchParams);
          next.set("customerId", id);
          navigate(`/?${next.toString()}`, { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [location.pathname, navigate, searchParams, setCustomerId, setSelectedCustomerId, setSelectedCustomerName]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const socialPostsTab = searchParams.get("tab");
  const isConnectionsActive =
    location.pathname === "/social-posts" && socialPostsTab === "connect";
  const isAnalyticsActive =
    location.pathname === "/social-posts" && socialPostsTab === "analytics";
  // "Content" hub owns the Social + Blogs tabs; Connections/Analytics are their own rows.
  const isContentActive =
    location.pathname === "/social-posts" &&
    socialPostsTab !== "connect" &&
    socialPostsTab !== "analytics";

  // Clean, boxless nav row: active state is a subtle fill only — no border, no shadow.
  const navItemClass = (active: boolean) =>
    `flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors ${
      active
        ? "bg-theme-secondary/15 font-medium text-theme-fg"
        : "text-theme-fg-muted hover:bg-theme-secondary/10 hover:text-theme-fg"
    }`;

  const subItemClass =
    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[13px] text-theme-fg-muted hover:bg-theme-secondary/15 hover:text-theme-fg";

  const currentThreadId =
    location.pathname === "/" ? searchParams.get("threadId") : null;

  const handleNewChatFromSidebar = () => {
    const agentId = searchParams.get("agentId");
    const cid = selectedCustomerId ?? searchParams.get("customerId");
    if (agentId && cid) {
      navigate(`/?agentId=${agentId}&customerId=${cid}`);
    } else if (cid) {
      navigate(`/?customerId=${cid}`);
    } else {
      navigate("/");
    }
    onClose?.();
  };

  const handleThreadSelect = (threadId: string, agentId?: string, threadCustomerId?: string) => {
    const cid =
      threadCustomerId ?? selectedCustomerId ?? searchParams.get("customerId");
    if (!cid) return;
    if (selectedCustomerId !== cid) {
      setSelectedCustomerId(cid);
      setCustomerId(cid);
    }
    const aid = agentId ?? searchParams.get("agentId");
    if (aid) {
      navigate(
        `/?agentId=${encodeURIComponent(aid)}&customerId=${encodeURIComponent(cid)}&threadId=${encodeURIComponent(threadId)}`
      );
    } else {
      navigate(
        `/?customerId=${encodeURIComponent(cid)}&threadId=${encodeURIComponent(threadId)}`
      );
    }
    onClose?.();
  };

  const handleChatClick = () => {
    if (selectedCustomerId) {
      navigate(`/?customerId=${encodeURIComponent(selectedCustomerId)}`, {
        replace: true,
      });
    } else {
      navigate("/", { replace: true });
    }
    onClose?.();
    setAccountMenuOpen(false);
  };

  // Single source for nav entries — drives both the expanded list and the collapsed icon rail.
  const navItems = [
    { key: "chat", icon: "chat", label: "Chat", active: isActive("/"), onClick: handleChatClick },
    { key: "content", icon: "edit_note", label: "Content", active: isContentActive, onClick: () => handleNavigation("/social-posts") },
    { key: "media", icon: "photo_library", label: "Media Library", active: isActive("/media-library"), onClick: () => handleNavigation("/media-library") },
    { key: "connections", icon: "link", label: "Connections", active: isConnectionsActive, onClick: () => handleNavigation("/social-posts?tab=connect") },
    { key: "analytics", icon: "insights", label: "Analytics", active: isAnalyticsActive, onClick: () => handleNavigation("/social-posts?tab=analytics") },
    { key: "setup", icon: "auto_awesome", label: "Sagelyn AI Assistant", active: isActive("/onboarding-agent"), onClick: () => handleNavigation("/onboarding-agent") },
  ];

  // Collapsed desktop rail: icons only, labels surfaced as tooltips.
  if (collapsed) {
    const railItemClass = (active: boolean) =>
      `flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
        active
          ? "bg-theme-secondary/15 text-theme-fg"
          : "text-theme-fg-muted hover:bg-theme-secondary/10 hover:text-theme-fg"
      }`;

    return (
      <aside className="sidebar flex h-full max-h-full w-full min-w-0 shrink-0 flex-col items-center border-r border-theme-border bg-theme-surface py-2.5">
        <Link
          to="/"
          onClick={() => onClose?.()}
          className="mb-1 flex items-center rounded-lg p-0.5 hover:opacity-90"
          aria-label="Home"
          title="Home"
        >
          <img
            src="/sagelyn-circle.png"
            alt=""
            className="h-8 w-8 rounded-full object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </Link>
        <button
          type="button"
          onClick={() => (onToggleCollapse ? onToggleCollapse() : onClose?.())}
          className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-theme-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <span className="material-icons-outlined text-xl" aria-hidden>
            view_sidebar
          </span>
        </button>

        <ConnectionAlertBell className="mb-1" menuPosition="right-start" />

        <nav className="flex w-full flex-1 flex-col items-center gap-1 overflow-y-auto px-2 py-1">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              title={item.label}
              aria-label={item.label}
              onClick={item.onClick}
              className={railItemClass(item.active)}
            >
              <span className="material-icons-outlined text-xl" aria-hidden>
                {item.icon}
              </span>
            </button>
          ))}
        </nav>

        <button
          type="button"
          onClick={() => (onToggleCollapse ? onToggleCollapse() : onClose?.())}
          title={user || "User"}
          aria-label="Account and settings"
          className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-theme-secondary/15 text-brand-person-icon transition-colors hover:bg-theme-secondary/25"
        >
          <span className="material-icons-outlined text-lg" aria-hidden>
            person
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="sidebar flex h-full max-h-full w-full min-w-0 shrink-0 flex-col border-r border-theme-border bg-theme-surface">
      <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2.5">
        <Link
          to="/"
          onClick={() => onClose?.()}
          className="flex min-w-0 items-center rounded-lg p-0.5 hover:opacity-90"
          aria-label="Home"
        >
          <img
            src="/sagelyn-circle.png"
            alt=""
            className="h-9 w-9 shrink-0 rounded-full object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </Link>
        <div className="flex shrink-0 items-center gap-1">
          <ConnectionAlertBell menuPosition="below-left" />
          <button
            type="button"
            onClick={() => (onToggleCollapse ? onToggleCollapse() : onClose?.())}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-theme-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <span className="material-icons-outlined text-xl" aria-hidden>
              view_sidebar
            </span>
          </button>
        </div>
      </div>

      <div className="shrink-0 px-3 pb-2.5 pt-1">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-theme-fg-muted">
          Business profile
        </p>
        <CustomerDropdown
          onCustomerSelect={handleCustomerChange}
          idToken={idToken || ""}
          compact
          selectedCustomerId={selectedCustomerId ?? ""}
        />
      </div>

      <nav className="max-h-[min(42vh,22rem)] shrink-0 space-y-0.5 overflow-y-auto px-3 pb-2">
        {navItems.map((item) => (
          <button
            key={item.key}
            type="button"
            className={navItemClass(item.active)}
            onClick={item.onClick}
          >
            <span className="material-icons-outlined text-lg text-theme-fg-muted">
              {item.icon}
            </span>
            {item.label}
          </button>
        ))}
      </nav>

      <div
        className={`flex min-h-0 flex-col px-3 pt-1 pb-1 ${
          historyOpen ? "min-h-0 flex-1" : "shrink-0"
        }`}
      >
        <div className="mb-1 flex items-center gap-2 px-0.5">
          <p
            id="sidebar-history-heading"
            className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-theme-fg-muted"
          >
            History
          </p>
          <button
            type="button"
            onClick={() => { setHistorySearchOpen((o) => !o); if (historySearchOpen) setHistorySearchQuery(""); if (!historyOpen) setHistoryOpen(true); }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-theme-fg"
            title="Search history"
          >
            <span className="material-icons text-[16px]" aria-hidden>search</span>
          </button>
          <button
            type="button"
            aria-expanded={historyOpen}
            aria-controls="sidebar-history-threads"
            onClick={() => setHistoryOpen((o) => !o)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-theme-fg"
            title={historyOpen ? "Hide history" : "Show history"}
          >
            <span className="material-icons text-lg" aria-hidden>
              {historyOpen ? "expand_less" : "expand_more"}
            </span>
          </button>
        </div>

        {historySearchOpen && (
          <div className="mb-1.5 flex items-center gap-2.5 rounded-full bg-theme-secondary/8 px-3 py-2">
            <span className="material-icons shrink-0 text-[18px] text-theme-fg-muted/50">search</span>
            <input
              type="text"
              placeholder="Search threads…"
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
              onBlur={() => {
                if (!historySearchQuery) setHistorySearchOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setHistorySearchQuery("");
                  setHistorySearchOpen(false);
                }
              }}
              style={{ outline: "none", boxShadow: "none" }}
              className="min-w-0 flex-1 border-none bg-transparent text-xs text-theme-fg placeholder:text-theme-fg-muted/60"
              autoFocus
            />
            {historySearchQuery && (
              <button
                type="button"
                onClick={() => { setHistorySearchQuery(""); setHistorySearchOpen(false); }}
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-theme-fg-muted/20 text-theme-fg-muted hover:bg-theme-fg-muted/30 hover:text-theme-fg"
              >
                <span className="material-icons text-[12px]">close</span>
              </button>
            )}
          </div>
        )}

        <div
          id="sidebar-history-threads"
          role="region"
          aria-labelledby="sidebar-history-heading"
          className={
            historyOpen
              ? "min-h-0 flex-1 overflow-hidden rounded-lg"
              : "hidden"
          }
        >
          {idToken && (selectedCustomerId || searchParams.get("customerId")) ? (
            <ConversationSidebar
              idToken={idToken}
              customerId={
                selectedCustomerId ?? searchParams.get("customerId")!
              }
              agentId={searchParams.get("agentId")}
              currentThreadId={currentThreadId}
              onThreadSelect={handleThreadSelect}
              onNewChat={handleNewChatFromSidebar}
              refreshTrigger={threadListVersion}
              shellVariant
              searchQuery={historySearchQuery}
              className="h-full min-h-[120px] !bg-transparent"
            />
          ) : (
            <div className="p-3 text-center text-xs text-theme-fg-muted">
              Select a business profile to see threads.
            </div>
          )}
        </div>
      </div>

      <div
        ref={accountMenuRef}
        className="relative isolate mt-auto shrink-0 border-t border-theme-border p-3"
      >
        {accountMenuOpen && (
          <div
            className="absolute bottom-full left-3 right-3 z-[100] mb-1 space-y-0.5 rounded-xl border border-theme-border bg-theme-surface p-1 shadow-[0_12px_40px_-4px_rgba(0,0,0,0.14),0_4px_16px_-4px_rgba(0,0,0,0.08)] ring-1 ring-theme-border/90 dark:shadow-[0_12px_40px_-4px_rgba(0,0,0,0.45)] dark:ring-theme-border"
            role="menu"
            aria-label="Account and settings"
          >
            <p className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
              Settings
            </p>
            <button
              type="button"
              role="menuitem"
              className={subItemClass}
              onClick={() => handleNavigation("/users")}
            >
              <span className="material-icons text-base text-zinc-400">
                person
              </span>
              My profile
            </button>
            <button
              type="button"
              role="menuitem"
              className={subItemClass}
              onClick={() => handleNavigation("/customer")}
            >
              <span className="material-icons text-base text-zinc-400">
                business
              </span>
              Business settings
            </button>
            <button
              type="button"
              role="menuitem"
              className={subItemClass}
              onClick={() => handleNavigation("/billing")}
            >
              <span className="material-icons text-base text-zinc-400">
                account_balance_wallet
              </span>
              Credits &amp; Billing
            </button>
            {canManageAllUsers && (
              <button
                type="button"
                role="menuitem"
                className={subItemClass}
                onClick={() => handleNavigation("/users/all")}
              >
                <span className="material-icons text-base text-zinc-400">
                  groups
                </span>
                All users
              </button>
            )}
            <a
              role="menuitem"
              href="https://www.sagelyn.ai/docs"
              target="_blank"
              rel="noopener noreferrer"
              className={subItemClass}
              onClick={() => setAccountMenuOpen(false)}
            >
              <span className="material-icons text-base text-zinc-400">
                help_outline
              </span>
              Help &amp; docs
            </a>
            <div className="my-1 border-t border-theme-border" />
            <AppearanceMenu subItemClass={subItemClass} />
            <div className="my-1 border-t border-theme-border" />
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
            >
              <span className="material-icons text-base">logout</span>
              Log out
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setAccountMenuOpen((o) => !o)}
          className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-theme-secondary/10 ${
            accountMenuOpen ? "bg-theme-secondary/15" : ""
          }`}
          aria-expanded={accountMenuOpen}
          aria-haspopup="true"
          aria-label="Open settings and account"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-theme-secondary/15"
              aria-hidden
            >
              <span className="material-icons-outlined text-lg text-brand-person-icon">
                person
              </span>
            </div>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-theme-fg">
                {user || "User"}
              </p>
              <p className="text-[10px] text-theme-fg-muted">Account &amp; settings</p>
            </div>
          </div>
          <span
            className={`material-icons shrink-0 text-base text-theme-fg-muted transition-transform ${
              accountMenuOpen ? "rotate-180" : ""
            }`}
          >
            expand_more
          </span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
